import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import {
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import {
  fetchBenefitsPayments,
  filterPaymentsByMonth,
  getPaidAmount,
  getTotalAmount,
  isCountedPayment,
} from '../../utils/benefitsFinance';
import { FORMATION_PAYMENTS_QUERY_KEY } from '../../utils/queryKeys';

const RevenueAnalysisSimplified = () => {
  const [month, setMonth] = useState(new Date().getMonth() + 1 < 10 ? `0${new Date().getMonth() + 1}` : `${new Date().getMonth() + 1}`);
  const [year, setYear] = useState(new Date().getFullYear().toString());

  const { data: payments = [], isLoading: paymentsLoading } = useQuery(
    FORMATION_PAYMENTS_QUERY_KEY,
    fetchBenefitsPayments,
    {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
    }
  );

  const paymentStatusBreakdown = React.useMemo(() => {
    const selectedYear = parseInt(year, 10);
    const selectedMonth = parseInt(month, 10);
    const filteredPayments = filterPaymentsByMonth(payments, selectedYear, selectedMonth);

    const breakdown = {
      totalPaid: 0,
      totalPartial: 0,
      totalOutstanding: 0,
      paidCount: 0,
      partialCount: 0
    };

    filteredPayments.forEach(payment => {
      const totalAmount = getTotalAmount(payment);
      const paidAmount = getPaidAmount(payment);
      const isPaid = payment.isPaid || (totalAmount > 0 && paidAmount >= totalAmount);

      if (isPaid) {
        breakdown.totalPaid += paidAmount;
        breakdown.paidCount += 1;
      } else if (paidAmount > 0) {
        breakdown.totalPartial += paidAmount;
        breakdown.totalOutstanding += Math.max(0, totalAmount - paidAmount);
        breakdown.partialCount += 1;
      } else if (totalAmount > 0) {
        breakdown.totalOutstanding += totalAmount;
      }
    });

    return breakdown;
  }, [payments, month, year]);

  const monthlyTrends = React.useMemo(() => {
    const currentYear = parseInt(year, 10);

    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];

    return months.map((monthName, index) => {
      const monthPayments = filterPaymentsByMonth(payments, currentYear, index + 1);
      const monthRevenue = monthPayments.reduce((total, payment) => {
        if (!isCountedPayment(payment)) return total;
        return total + getPaidAmount(payment);
      }, 0);

      return {
        month: monthName,
        revenue: monthRevenue
      };
    });
  }, [payments, year]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const totalRevenue = paymentStatusBreakdown.totalPaid + paymentStatusBreakdown.totalPartial;

  return (
    <div className="space-y-6">
      {/* En-tête avec sélection de période */}
      <div className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-200 dark:border-slate-600">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center">
            <ArrowTrendingUpIcon className="h-6 w-6 text-green-400 mr-2" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">Revenus formations</h2>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-slate-50"
            >
              <option value="01" className="bg-slate-800">Janvier</option>
              <option value="02" className="bg-slate-800">Février</option>
              <option value="03" className="bg-slate-800">Mars</option>
              <option value="04" className="bg-slate-800">Avril</option>
              <option value="05" className="bg-slate-800">Mai</option>
              <option value="06" className="bg-slate-800">Juin</option>
              <option value="07" className="bg-slate-800">Juillet</option>
              <option value="08" className="bg-slate-800">Août</option>
              <option value="09" className="bg-slate-800">Septembre</option>
              <option value="10" className="bg-slate-800">Octobre</option>
              <option value="11" className="bg-slate-800">Novembre</option>
              <option value="12" className="bg-slate-800">Décembre</option>
            </select>
            
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-slate-50"
            >
              <option value="2025" className="bg-slate-800">2025</option>
              <option value="2024" className="bg-slate-800">2024</option>
              <option value="2023" className="bg-slate-800">2023</option>
              <option value="2022" className="bg-slate-800">2022</option>
            </select>
          </div>
        </div>
        
        <div className="mt-4 text-sm text-slate-600 dark:text-slate-400">
          Période sélectionnée: {month}/{year}
        </div>
      </div>

      {/* État des paiements */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.4 }}
        className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-200 dark:border-slate-600"
      >
        <div className="flex items-center mb-4">
          <CurrencyDollarIcon className="h-6 w-6 text-yellow-400 mr-2" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">État des paiements</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-green-300 font-medium">Paiements Complets</h3>
                <p className="text-sm text-green-400/80">{paymentStatusBreakdown.paidCount} transactions</p>
              </div>
              <CheckCircleIcon className="h-8 w-8 text-green-400" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-50">
              {formatCurrency(paymentStatusBreakdown.totalPaid)} DA
            </p>
            <div className="mt-2">
              <div className="w-full bg-green-500/10 h-2 rounded-full">
                <div 
                  className="h-2 rounded-full bg-green-500"
                  style={{ 
                    width: `${totalRevenue > 0 ? (paymentStatusBreakdown.totalPaid / totalRevenue * 100) : 0}%` 
                  }}
                ></div>
              </div>
              <p className="text-xs text-green-400 mt-1">
                {totalRevenue > 0 ? Math.round((paymentStatusBreakdown.totalPaid / totalRevenue) * 100) : 0}% du total
              </p>
            </div>
          </div>

          <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-orange-300 font-medium">Paiements Partiels</h3>
                <p className="text-sm text-orange-400/80">{paymentStatusBreakdown.partialCount} transactions</p>
              </div>
              <ClockIcon className="h-8 w-8 text-orange-400" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-50">
              {formatCurrency(paymentStatusBreakdown.totalPartial)} DA
            </p>
            <div className="mt-2">
              <div className="w-full bg-orange-500/10 h-2 rounded-full">
                <div 
                  className="h-2 rounded-full bg-orange-500"
                  style={{ 
                    width: `${totalRevenue > 0 ? (paymentStatusBreakdown.totalPartial / totalRevenue * 100) : 0}%` 
                  }}
                ></div>
              </div>
              <p className="text-xs text-orange-400 mt-1">
                {totalRevenue > 0 ? Math.round((paymentStatusBreakdown.totalPartial / totalRevenue) * 100) : 0}% du total
              </p>
            </div>
          </div>

          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-red-300 font-medium">Montants En Attente</h3>
                <p className="text-sm text-red-400/80">À recouvrer</p>
              </div>
              <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-50">
              {formatCurrency(paymentStatusBreakdown.totalOutstanding)} DA
            </p>
            <div className="mt-2">
              <div className="w-full bg-red-500/10 h-2 rounded-full">
                <div 
                  className="h-2 rounded-full bg-red-500"
                  style={{ 
                    width: `${(paymentStatusBreakdown.totalPaid + paymentStatusBreakdown.totalPartial + paymentStatusBreakdown.totalOutstanding) > 0 ? 
                      (paymentStatusBreakdown.totalOutstanding / (paymentStatusBreakdown.totalPaid + paymentStatusBreakdown.totalPartial + paymentStatusBreakdown.totalOutstanding) * 100) : 0}%` 
                  }}
                ></div>
              </div>
              <p className="text-xs text-red-400 mt-1">
                Potentiel de recouvrement
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 p-4 bg-slate-100/80 dark:bg-slate-800/40 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-slate-900 dark:text-slate-50 font-medium">Total Période Sélectionnée</h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                {paymentStatusBreakdown.paidCount + paymentStatusBreakdown.partialCount} transactions
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-50">
                {formatCurrency(totalRevenue + paymentStatusBreakdown.totalOutstanding)} DA
              </p>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Montant total contractuel</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Évolution des revenus */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.7 }}
        className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-sm rounded-xl p-6 border border-slate-200 dark:border-slate-600"
      >
        <div className="flex items-center mb-4">
          <ArrowTrendingUpIcon className="h-6 w-6 text-blue-400 mr-2" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">Évolution des revenus</h2>
        </div>

        <div className="relative h-72 mt-4 bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          {monthlyTrends.length > 0 && monthlyTrends.some(trend => trend.revenue > 0) ? (
            <div className="h-full">
              {/* Chart Title */}
              <div className="text-center mb-4">
                <h3 className="text-slate-900 dark:text-slate-50 font-medium">Évolution des revenus par mois ({year})</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm">Revenus totaux collectés chaque mois</p>
              </div>
              
              {/* Chart Container */}
              <div className="flex items-end justify-center h-48 gap-2 px-4">
                {monthlyTrends.map((item, index) => {
                  const maxRevenue = Math.max(...monthlyTrends.map(m => m.revenue), 1);
                  const barHeight = maxRevenue > 0 ? (item.revenue / maxRevenue) * 160 : 0;
                  const monthNames = [
                    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
                  ];
                  const isCurrentMonth = item.month === monthNames[parseInt(month) - 1];
                  
                  return (
                    <div key={`chart-${item.month}-${index}`} className="flex flex-col items-center min-w-0 flex-1">
                      {/* Revenue Value */}
                      <div className="mb-2 text-center">
                        <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                          {item.revenue > 999999 ? 
                            `${(item.revenue / 1000000).toFixed(1)}M` : 
                            item.revenue > 999 ? 
                            `${(item.revenue / 1000).toFixed(0)}K` : 
                            item.revenue.toFixed(0)}
                        </p>
                        <p className="text-xs text-slate-600 dark:text-slate-400">DA</p>
                      </div>
                      
                      {/* Bar */}
                      <div
                        className={`w-full rounded-t-lg transition-all duration-500 hover:opacity-80 ${
                          isCurrentMonth 
                            ? 'bg-gradient-to-t from-blue-600 to-blue-400' 
                            : index > 0 && item.revenue > monthlyTrends[index-1].revenue
                            ? 'bg-gradient-to-t from-green-600 to-green-400'
                            : index > 0 && item.revenue < monthlyTrends[index-1].revenue
                            ? 'bg-gradient-to-t from-red-600 to-red-400'
                            : 'bg-gradient-to-t from-gray-600 to-gray-400'
                        }`}
                        style={{ 
                          height: `${Math.max(barHeight, 8)}px`,
                          minHeight: '8px'
                        }}
                        title={`${item.month}: ${formatCurrency(item.revenue)} DA`}
                      ></div>
                      
                      {/* Month Label */}
                      <div className="mt-2 text-center">
                        <p className={`text-xs font-medium ${
                          isCurrentMonth ? 'text-blue-400' : 'text-slate-600 dark:text-slate-400'
                        }`}>
                          {item.month.substring(0, 3)}
                        </p>
                        {/* Growth Indicator */}
                        {index > 0 && (
                          <div className="flex items-center justify-center mt-1">
                            {item.revenue > monthlyTrends[index-1].revenue ? (
                              <ArrowTrendingUpIcon className="h-3 w-3 text-green-400" />
                            ) : item.revenue < monthlyTrends[index-1].revenue ? (
                              <ArrowTrendingDownIcon className="h-3 w-3 text-red-400" />
                            ) : (
                              <div className="h-3 w-3"></div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Chart Legend */}
              <div className="mt-4 flex flex-wrap justify-center gap-4 text-xs">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
                  <span className="text-slate-600 dark:text-slate-400">Mois sélectionné</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
                  <span className="text-slate-600 dark:text-slate-400">Croissance</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded mr-2"></div>
                  <span className="text-slate-600 dark:text-slate-400">Décroissance</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <ArrowTrendingUpIcon className="h-12 w-12 text-slate-900 dark:text-slate-50/20 mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400">Aucune donnée de revenus disponible</p>
                <p className="text-slate-400 dark:text-slate-500 text-sm mt-2">Ajoutez des paiements pour voir l'évolution</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default RevenueAnalysisSimplified;
